import {useMemo} from "react";
import {useParams} from "react-router-dom";
import {useImages} from "contexts/images";
import {useSetting} from "contexts/setting";
import {useBgData, type BgUrls} from "hooks/useBgData";
import {useBgCatalogFallbackIndex, useBgCatalogIndex} from "hooks/useBgCatalogIndex";
import {useBgDerivation} from "hooks/useBgDerivation";
import {findBgUrl} from "libs/bg-utils";
import {getGdnBgTilesets} from "libs/gdn-bg";
import {bgMarkersFromUrl} from "libs/bgMarkers";
import {lookupBgImage, type BgCatalogIndex} from "libs/bgCatalogIndex";
import {activePinnedBgUrl, type PinnedSceneBg} from "libs/sceneBgSource";

export type BgUrlResult = {
  /** Resolved URL. Empty string when no match found. */
  url: string;
  /** Same as `url` but for the -mini thumbnail variant. */
  urlMini: string;
  /** True when the primary was missing and resolution fell back to -outdated. */
  isOutdated: boolean;
  /** True when no URL resolved (e.g. bad product/bg combo, or custom without upload). */
  isMissing: boolean;
  /** True only while the source that can resolve the URL is still pending. */
  isLoading?: boolean;
  /**
   * Style markers of the resolved background (libs/bgMarkers.ts) — the app's
   * marker classNames and the logo variant both come from here. Read from the
   * catalog when it's the source, parsed from the filename on the legacy path:
   * one place that knows which of the two applies (issue #567).
   */
  markers: string[];
};

const NO_MARKERS: string[] = [];

/** Pure core — no React. Exported so unit tests don't need hook mocking. */
export function deriveBgUrl({
  bgOption,
  productValue,
  allBgUrls,
  customBg,
  catalogIndex,
  catalogFallbackIndex,
  pinnedUrl,
}: {
  bgOption: string | number | null | undefined;
  productValue: string;
  allBgUrls: BgUrls;
  customBg?: string | null;
  /**
   * The v3 display-image index (issue #567, B3). When present it is
   * authoritative and no filename is inspected — required once the bytes move to
   * content-hashed keys, where `/<product>` no longer appears in a url. Null on
   * the legacy path.
   */
  catalogIndex?: BgCatalogIndex | null;
  /**
   * The same index consulted AFTER the legacy url array, only when that array
   * has nothing for the product (issue #698). A catalog-only background — one
   * with no `Backgrounds/<channel>/<type>/<n>/<PRODUCT>*.png` tile behind it,
   * such as JetBrains Air's re-hosted artwork or a promoted upload — has no
   * filename to match, so the legacy path resolved it to "" and every saved
   * scene, and therefore every server export, rendered without it. The legacy
   * array keeps precedence, so a scene with a tile behind it renders exactly as
   * before; the catalog only fills the gap the tiles leave.
   */
  catalogFallbackIndex?: BgCatalogIndex | null;
  /**
   * The image a saved scene pins for this variation (libs/sceneBgSource.ts
   * `activePinnedBgUrl`). It wins over the tiles and the catalog: it is what
   * the author saw. Mini and markers come from the catalog entry only when
   * that entry is still the same image.
   */
  pinnedUrl?: string | null;
}): BgUrlResult {
  if (bgOption === "custom") {
    return {
      url: customBg ?? "",
      urlMini: "",
      isOutdated: false,
      // Custom mode with no uploaded image is a missing state — callers
      // shouldn't have to double-check `url === ""` alongside isMissing.
      isMissing: !customBg,
      // An upload has no catalog entry, so its filename is all there is —
      // the same thing the legacy path did with it.
      markers: bgMarkersFromUrl(customBg),
    };
  }

  const idx = Number(bgOption);
  if (!Number.isInteger(idx) || idx <= 0) {
    return {url: "", urlMini: "", isOutdated: false, isMissing: true, markers: NO_MARKERS};
  }

  if (pinnedUrl) {
    const entry = lookupBgImage(catalogIndex ?? catalogFallbackIndex, idx, productValue);
    const sameImage = entry?.url === pinnedUrl;
    return {
      url: pinnedUrl,
      urlMini: sameImage ? (entry.miniUrl ?? "") : "",
      isOutdated: false,
      isMissing: false,
      markers: sameImage ? (entry.markers ?? NO_MARKERS) : bgMarkersFromUrl(pinnedUrl),
    };
  }

  if (catalogIndex) {
    const entry = lookupBgImage(catalogIndex, idx, productValue);
    const markers = entry?.markers ?? NO_MARKERS;
    if (entry?.url) {
      return {
        url: entry.url,
        urlMini: entry.miniUrl ?? "",
        isOutdated: false,
        isMissing: false,
        markers,
      };
    }
    // Same precedence as the legacy path below: primary → -outdated → missing,
    // and ALL is never a product of its own. The outdated variant has no mini
    // (the catalog files `<product>-mini-outdated` under its own key, which the
    // legacy list didn't expose under the flag either).
    const outdatedUrl = productValue === "ALL" ? null : (entry?.outdatedUrl ?? null);
    return {
      url: outdatedUrl ?? "",
      urlMini: "",
      isOutdated: Boolean(outdatedUrl),
      isMissing: !outdatedUrl,
      markers,
    };
  }

  // The catalog's entry for this product, used only where the tiles have none.
  const fromCatalogFallback = (): BgUrlResult | null => {
    const entry = lookupBgImage(catalogFallbackIndex, idx, productValue);
    if (!entry?.url) return null;
    return {
      url: entry.url,
      urlMini: entry.miniUrl ?? "",
      isOutdated: false,
      isMissing: false,
      markers: entry.markers ?? NO_MARKERS,
    };
  };

  if (allBgUrls.length === 0) {
    return (
      fromCatalogFallback() ?? {
        url: "",
        urlMini: "",
        isOutdated: false,
        isMissing: true,
        markers: NO_MARKERS,
      }
    );
  }

  const primary = findBgUrl(allBgUrls, idx, `/${productValue}`);
  const primaryMini = findBgUrl(allBgUrls, idx, `/${productValue}-mini`);
  if (primary) {
    return {
      url: primary,
      urlMini: primaryMini ?? "",
      isOutdated: false,
      isMissing: false,
      markers: bgMarkersFromUrl(primary),
    };
  }

  const isAllProductsMode = productValue === "ALL";
  if (isAllProductsMode) {
    return {url: "", urlMini: "", isOutdated: false, isMissing: true, markers: NO_MARKERS};
  }

  const outdated = findBgUrl(allBgUrls, idx, `/${productValue}-outdated`);
  const outdatedMini = findBgUrl(allBgUrls, idx, `/${productValue}-mini-outdated`);
  if (outdated) {
    return {
      url: outdated,
      urlMini: outdatedMini ?? "",
      isOutdated: true,
      isMissing: false,
      markers: bgMarkersFromUrl(outdated),
    };
  }

  return (
    fromCatalogFallback() ?? {
      url: "",
      urlMini: "",
      isOutdated: false,
      isMissing: true,
      markers: NO_MARKERS,
    }
  );
}

/**
 * Derives the currently-active background URL + its mini variant from the
 * React Query cache, URL-bar bgOption, and selected product.
 *
 * The RQ cache is keyed on (channel, type) only (API is product-agnostic),
 * so child Setting.Provider overrides for per-product rendering — e.g.
 * AllProductsWhatsNew rendering one banner per product — share the parent's
 * tile-set cache entry without any extra wiring.
 */
export function useBgUrl(): BgUrlResult {
  type Selection = {value: string};
  // The background cache is keyed by the pair App.js fetched under, which is
  // the product's home scope — one background list for an offering that two
  // channels list (src/data/offering.ts).
  const {bgOption, product, dataChannel, dataType, pinnedSceneBg} = useSetting() as unknown as {
    bgOption: string | number | null | undefined;
    product: Selection | null;
    dataChannel: string;
    dataType: string;
    pinnedSceneBg: PinnedSceneBg | null;
  };
  const {sceneId} = useParams();
  const {customBg} = useImages();

  const channelValue = dataChannel;
  const typeValue = dataType;
  // Ads "Google Display Network" uses its bundled v3-upload manifest (see
  // libs/gdn-bg.ts); its query/cache identity remains ads/google-display-network.
  const isGoogleDisplayNetwork = channelValue === "ads" && typeValue === "google-display-network";

  // Passive observer — App.js's useBackgrounds owns the fetch with its own
  // gating (channel-gate on scene routes, etc.). Firing the query from here
  // would ignore that gate and re-introduce the wrong-channel fetch #238
  // fixed. We still re-render when the shared cache updates.
  const {
    allBgUrls: cachedBgUrls,
    isPending,
    isSuccess,
  } = useBgData(channelValue, typeValue, {enabled: false});

  // GDN reads the bundled v3 manifest (product-agnostic, Kraken tile_sets
  // shape) so findBgUrl/deriveBgUrl resolve it exactly like a Social tileset.
  const allBgUrls: BgUrls = isGoogleDisplayNetwork ? getGdnBgTilesets() : cachedBgUrls;
  // Published by App under bgDerivation; null on the legacy path (#567 B3).
  const catalogIndex = useBgCatalogIndex(channelValue, typeValue);
  // The same entry read without the derivation gate, for the legacy path's
  // catalog-only backgrounds (#698). Authoritative above, a fallback here.
  const catalogFallbackIndex = useBgCatalogFallbackIndex(channelValue, typeValue);
  const bgDerivation = useBgDerivation();

  if (!product) throw new Error("Background URL resolution requires a selected product");
  const productValue = product.value;

  const pinnedUrl = activePinnedBgUrl(pinnedSceneBg, {sceneId, bgOption, productValue});

  return useMemo(() => {
    const resolved = deriveBgUrl({
      bgOption,
      productValue,
      allBgUrls,
      customBg,
      catalogIndex,
      catalogFallbackIndex: catalogIndex ? null : catalogFallbackIndex,
      pinnedUrl,
    });
    const needsCatalogOrList = bgOption !== "custom" && bgOption !== "generative";
    const isLoading =
      !pinnedUrl &&
      needsCatalogOrList &&
      (bgDerivation ? catalogIndex === null : isPending && !isSuccess);
    return {...resolved, isLoading};
  }, [
    bgOption,
    productValue,
    allBgUrls,
    customBg,
    catalogIndex,
    catalogFallbackIndex,
    pinnedUrl,
    bgDerivation,
    isPending,
    isSuccess,
  ]);
}
